/**
 * A3 — Repetir cita: cada N semanas × veces (tope 52 / 12 meses). Vista previa con conflictos y
 * alternativas por fecha (elegir otra hora o saltar) antes de crear la serie.
 */
import { useEffect, useState } from 'react';
import { Repeat, AlertTriangle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { friendlyError } from '@/lib/apiError';
import { formatDateShortEs, formatTime12 } from '@/utils/bookingFormat';
import { tr } from '@/contexts/LanguageContext';

const selCls = 'h-9 rounded-lg border border-border bg-background px-2 text-xs text-foreground';

export const RecurringPicker = ({ value, onChange }) => (
  <div className="rounded-xl border border-border p-3 space-y-2" data-testid="recurring-picker">
    <label className="flex items-center gap-2 text-sm font-semibold">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked ? { every: 1, count: 4 } : null)} className="accent-[#C9A84C] w-4 h-4" data-testid="recurring-toggle" />
      <Repeat className="w-4 h-4 text-primary" /> {tr("Repetir esta cita")}
    </label>
    {value && (
      <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="recurring-options">
        <span>{tr("Cada")}</span>
        <select value={value.every} onChange={(e) => onChange({ ...value, every: Number(e.target.value) })} className={selCls} data-testid="recurring-every">
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n === 1 ? tr("semana") : tr("{0} semanas", n)}</option>)}
        </select>
        <span>·</span>
        <select value={value.count} onChange={(e) => onChange({ ...value, count: Number(e.target.value) })} className={selCls} data-testid="recurring-count">
          {Array.from({ length: 51 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{tr("{0} veces", n)}</option>)}
        </select>
        <p className="w-full text-[10px] text-muted-foreground">{tr("Máximo 52 repeticiones o 12 meses. El cliente recibe un solo correo con todas las fechas.")}</p>
      </div>
    )}
  </div>
);

// Vista previa + creación. `base` = payload de la cita (barber_id, service_id(s), date, start_time, cliente…).
export const RecurringPreview = ({ base, recurring, onDone, onBack }) => {
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [skip, setSkip] = useState([]);
  const [busy, setBusy] = useState(false);
  const body = { ...base, every_weeks: recurring.every, count: recurring.count };

  useEffect(() => {
    let alive = true;
    setPreview(null); setOverrides({}); setSkip([]); setBusy(true);
    api.post('/bookings/series/preview', body)
      .then((r) => { if (alive) setPreview(r); })
      .catch((e) => { if (alive) toast.error(friendlyError(e, tr("No pudimos calcular la serie"))); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurring.every, recurring.count, base.date, base.start_time]);

  const unresolved = (preview?.items || []).filter((i) => i.status === 'conflict' && !overrides[i.date] && !skip.includes(i.date));
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/bookings/series', { ...body, overrides, skip });
      toast.success(tr("Serie creada · {0} citas", r.total));
      onDone(r);
    } catch (e) { toast.error(friendlyError(e, tr("No pudimos crear la serie"))); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="recurring-preview">
      <p className="text-sm font-semibold">{tr("Vista previa de la serie")}</p>
      {!preview && busy && <p className="text-xs text-muted-foreground" data-testid="recurring-loading">{tr("Calculando…")}</p>}
      {preview && (
        <>
          <p className="text-[11px] text-muted-foreground" data-testid="recurring-summary">
            {tr("{0} citas · {1} min · {2} con conflicto", preview.total, preview.duration_minutes, preview.conflicts)}
          </p>
          <ul className="space-y-1 max-h-60 overflow-y-auto" data-testid="recurring-items">
            {preview.items.map((it) => {
              const skipped = skip.includes(it.date);
              const ok = it.status === 'ok' || overrides[it.date];
              return (
                <li key={it.date} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs ${skipped ? 'opacity-50 border-border' : it.status === 'conflict' && !overrides[it.date] ? 'border-amber-500/60 bg-amber-500/5' : 'border-border'}`} data-testid={`recurring-item-${it.date}`} data-status={skipped ? 'skipped' : ok ? 'ok' : it.status}>
                  {skipped ? <X className="w-3.5 h-3.5 text-muted-foreground" /> : ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                  <span className="flex-1 first-letter:uppercase">{formatDateShortEs(it.date)} · {formatTime12(overrides[it.date] || it.start_time)}</span>
                  {it.status !== 'ok' && !skipped && (
                    <select value={overrides[it.date] || ''} onChange={(e) => setOverrides((o) => ({ ...o, [it.date]: e.target.value || undefined }))} className={selCls} data-testid={`recurring-alt-${it.date}`}>
                      <option value="">{it.status === 'closed' ? tr("Cerrado · elegir hora") : tr("Conflicto · otra hora")}</option>
                      {it.alternatives.map((a) => <option key={a} value={a}>{formatTime12(a)}</option>)}
                    </select>
                  )}
                  <button type="button" onClick={() => setSkip((s) => (skipped ? s.filter((d) => d !== it.date) : [...s, it.date]))} className="text-[10px] text-primary underline" data-testid={`recurring-skip-${it.date}`}>
                    {skipped ? tr("Incluir") : tr("Saltar")}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={onBack} disabled={busy} data-testid="recurring-back">{tr("Atrás")}</Button>
            <Button className="flex-1 rounded-full" onClick={create} disabled={busy || unresolved.length > 0} data-testid="recurring-create">
              {busy ? tr("Guardando…") : unresolved.length ? tr("Resuelve {0} conflicto(s)", unresolved.length) : tr("Crear {0} citas", preview.total - skip.length)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
